import { CandidateSearchSortInput } from '__generated__/graphql'
import {
    CandidateFilterState,
    defaultFilters,
    filterCodec,
    FILTER_KEYS,
} from './candidateFilterState'
import { toGraphQLFilters } from './toGraphQLFilters'
import { codec, useUrlState } from './useUrlState'

export {
    areFiltersEqual,
    type CandidateFilterState,
    defaultFilters,
    type SearchFilterItem,
} from './candidateFilterState'
export { toGraphQLFilters } from './toGraphQLFilters'

const DEFAULT_LIMIT = 50

export type PaginationState = {
    page: number
    limit: number
}

export type CandidateSortField =
    | 'firstName'
    | 'lastName'
    | 'dateOnRoster'
    | 'processStatus'
    | 'processStatusUpdatedAt'
    | 'company'
    | 'sectorDescription'
    | 'currentTitle'
    | 'currentIndustry'
    | 'currentSector'
    | 'currentLocation'
    | 'startDate'
    | 'previousCompany'
    | 'previousIndustry'
    | 'previousSector'
    | 'gender'
    | 'ethnicity'
    | 'visaStatus'
    | 'cpiAttribute'
    | 'undergradCollege'
    | 'graduateCollege'
    | 'undergradYear'
    | 'undergradGpa'
    | 'sat'
    | 'act'
    | 'gmat'
    | 'hometown'
    | 'analystClassYear'
    | 'recruitingIndustryPref1'
    | 'recruitingIndustryPref2'
    | 'recruitingSectorPref1'
    | 'recruitingSectorPref2'
    | 'recruitingGeoPref1'
    | 'recruitingGeoPref2'
    | 'starRatingMostRecentScreen'
    | 'starRatingReference'
    | 'referenceAttribute'
    | 'preAdmitAttribute'
    | 'preferredEmail'
    | 'resumeUpdated'
    | 'internalNote'
    | 'starRating'
    | 'email'

export type SortOrder = 'asc' | 'desc'

const VALID_SORT_FIELDS: CandidateSortField[] = [
    'firstName',
    'lastName',
    'dateOnRoster',
    'processStatus',
    'processStatusUpdatedAt',
    'company',
    'sectorDescription',
    'currentTitle',
    'currentIndustry',
    'currentSector',
    'currentLocation',
    'startDate',
    'previousCompany',
    'previousIndustry',
    'previousSector',
    'gender',
    'ethnicity',
    'visaStatus',
    'cpiAttribute',
    'undergradCollege',
    'graduateCollege',
    'undergradYear',
    'undergradGpa',
    'sat',
    'act',
    'gmat',
    'hometown',
    'analystClassYear',
    'recruitingIndustryPref1',
    'recruitingIndustryPref2',
    'recruitingSectorPref1',
    'recruitingSectorPref2',
    'recruitingGeoPref1',
    'recruitingGeoPref2',
    'starRatingMostRecentScreen',
    'starRatingReference',
    'referenceAttribute',
    'preAdmitAttribute',
    'preferredEmail',
    'resumeUpdated',
    'internalNote',
    'starRating',
    'email',
]

export type CandidateSortState = {
    field: CandidateSortField
    order: SortOrder
}

export type SortState = CandidateSortState[]

const candidateSearchCodec = {
    page: codec.int(),
    limit: codec.int(),
    sort: {
        default: [] as CandidateSortState[],
        parse: (raw: string): CandidateSortState[] =>
            raw.split(',').flatMap((part) => {
                const sep = part.lastIndexOf(':')
                if (sep === -1) return []
                const field = part.slice(0, sep)
                const order = part.slice(sep + 1)
                if (
                    !VALID_SORT_FIELDS.includes(field as CandidateSortField) ||
                    (order !== 'asc' && order !== 'desc')
                )
                    return []
                return [
                    {
                        field: field as CandidateSortField,
                        order: order as SortOrder,
                    },
                ]
            }),
        serialize: (val: CandidateSortState[]): string | null =>
            val.length > 0
                ? val.map((s) => `${s.field}:${s.order}`).join(',')
                : null,
    },
    ...filterCodec,
}

const extractFilters = (
    state: Record<string, unknown>
): CandidateFilterState => {
    const filters = {} as Record<string, unknown>
    for (const key of FILTER_KEYS) {
        filters[key] = state[key]
    }
    return filters as unknown as CandidateFilterState
}

export function useCandidateSearchUrlState() {
    const { state, setState } = useUrlState(candidateSearchCodec)

    const page = state.page ?? 1
    const limit = state.limit ?? DEFAULT_LIMIT
    const sort = state.sort ?? ([] as CandidateSortState[])
    const filters = extractFilters(state as unknown as Record<string, unknown>)

    const hasActiveFilters = FILTER_KEYS.some((key) => {
        const serialize = filterCodec[key].serialize as (
            val: unknown
        ) => string | null
        return serialize((state as Record<string, unknown>)[key]) !== null
    })

    const graphQLSort: CandidateSearchSortInput[] | undefined =
        sort.length > 0
            ? sort.map((s) => ({ field: s.field, order: s.order }))
            : undefined

    const setPage = (newPage: number) =>
        setState({ page: newPage } as Partial<typeof state>)

    const setLimit = (newLimit: number) =>
        setState({ limit: newLimit, page: 1 } as Partial<typeof state>)

    const setFilters = (newFilters: CandidateFilterState) =>
        setState({ ...defaultFilters, ...newFilters, page: 1 } as Partial<
            typeof state
        >)

    const clearFilters = () =>
        setState({ ...defaultFilters, page: 1 } as Partial<typeof state>)

    const setSort = (newSort: SortState) =>
        setState({ sort: newSort, page: 1 } as Partial<typeof state>)

    return {
        page,
        limit,
        filters,
        graphQLFilters: toGraphQLFilters(filters),
        sort,
        graphQLSort,
        hasActiveFilters,
        setPage,
        setLimit,
        setFilters,
        clearFilters,
        setSort,
    }
}
